import { memo } from "react";
import { cn } from "../../../../../lib/cn";
import { CustomPopover } from "../../../../../components/shared/Popover";
import { formatToReadableTime } from "../../../../../utils/utils";
import {
  CheckIcon,
  EditIcon,
  DeleteIcon,
  ThreeDotIcon,
} from "../../../../../assets/icons";

export const SessionItem = memo(
  ({
    session,
    isActive,
    editingSessionId,
    editingTitle,
    onSelect,
    onSetEditingTitle,
    onSaveRename,
    onStartRename,
    onCancelEdit,
    onArchive,
    showProjectLabel,
    projectName,
    projectNameFallback,
  }) => {
    const updatedAt = session?.updated_at;
    const timestamp = updatedAt ? formatToReadableTime(updatedAt) : "";
    const preview = session?.last_message?.content || "";
    const isEditing = editingSessionId === session?.id;
    const hasProjectId = Boolean(session?.project_id);
    const resolvedProjectLabel =
      projectName ||
      (hasProjectId ? projectNameFallback || "Unknown project" : "No project");

    return (
      <div
        role="button"
        tabIndex={0}
        className={cn(
          "group flex items-start justify-between p-2.5 rounded-xl cursor-pointer mb-1 w-full text-left transition-all duration-200 border",
          isActive
            ? "bg-white border-primary/30 shadow-sm ring-1 ring-primary/10"
            : "bg-transparent border-transparent hover:bg-surface-hover hover:border-surface-border/50",
        )}
        onClick={() => onSelect(session?.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(session?.id);
          }
        }}
      >
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                value={editingTitle}
                onChange={(e) => onSetEditingTitle(e.target.value)}
                className="flex-1 min-w-0 text-sm font-medium bg-white border border-surface-border rounded px-2 py-1 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSaveRename(session);
                  if (e.key === "Escape") onCancelEdit?.();
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <p
                  className={cn(
                    "text-sm font-medium truncate flex-1 min-w-0",
                    isActive ? "text-primary" : "text-grey",
                  )}
                >
                  {session?.title || "Untitled Session"}
                </p>
              </div>
              {preview ? (
                <p
                  className={cn(
                    "text-[11px] truncate text-pretty",
                    isActive ? "text-mediumgrey" : "text-mediumgrey",
                  )}
                >
                  {preview}
                </p>
              ) : null}
              {showProjectLabel ? (
                <span className="inline-flex w-fit items-center gap-1 rounded-full bg-surface-hover px-2 py-0.5 text-[10px] text-mediumgrey">
                  {resolvedProjectLabel}
                </span>
              ) : null}
              {session?.status === "ARCHIVED" && (
                <span className="inline-block bg-surface-hover px-1.5 py-0.5 rounded text-[10px] text-mediumgrey w-fit">
                  Archived
                </span>
              )}
            </div>
          )}
        </div>

        <div
          className={cn(
            "flex items-center gap-1 shrink-0 transition-opacity",
            isActive || isEditing
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100",
          )}
        >
          {!isEditing && timestamp && (
            <span className="text-[10px] text-mediumgrey shrink-0 tabular-nums px-1">
              {timestamp}
            </span>
          )}
          {isEditing ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSaveRename(session);
              }}
              className="p-1.5 hover:bg-green-50 rounded-md text-green-600 transition-colors"
              title="Save"
              aria-label="Save session title"
            >
              <CheckIcon width={14} height={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStartRename(session);
              }}
              className="p-1.5 hover:bg-surface-hover rounded-md text-mediumgrey hover:text-darkgrey transition-colors"
              title="Rename"
              aria-label="Rename session"
            >
              <EditIcon width={14} height={14} />
            </button>
          )}

          <CustomPopover
            trigger={
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 hover:bg-surface-hover rounded-md text-mediumgrey hover:text-darkgrey transition-colors cursor-pointer"
                title="Options"
                aria-label="Session options"
              >
                <ThreeDotIcon width={14} height={14} />
              </button>
            }
            placement="bottom-end"
          >
            <div className="p-1 min-w-[120px] bg-white border border-surface-border rounded-lg shadow-lg">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive(session);
                }}
                className="w-full text-left text-xs px-3 py-2 hover:bg-danger/10 text-danger rounded-md flex items-center gap-2 transition-colors"
              >
                <DeleteIcon width={14} height={14} />
                Archive
              </button>
            </div>
          </CustomPopover>
        </div>
      </div>
    );
  },
);

SessionItem.displayName = "SessionItem";
